/* eslint-disable react-hooks/exhaustive-deps */
/* prettier-ignore */
import { useEffect, useState } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
	Box,
	Button,
	Flex,
	Text,
	useDisclosure,
	useToast,
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import Spreadsheet from 'react-spreadsheet';
import EWSLabels from '../../../ews-template-labels';
import Loading from '../../../../../../components/Loading';
import useStore from '../../../../../../store';
import { getYear } from '../../../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const Q_REPORT = gql`
	query getEarlyWarningNonLifeRatioReport($year: Float!, $companyType: InsuranceInstitutionsType!, $companyId: String!) {
		getEarlyWarningNonLifeRatioReport(year: $year, companyType: $companyType, companyId: $companyId)
	}
`;

const M_SAVE_NONLIFE_RATIO = gql`
	mutation saveCalculatedNonLifeRatioStatement(
		$year: Float!
		$companyType: InsuranceInstitutionsType!
		$companyId: String!
		$data: JSON
		$requiredSolvency: Float!
		$companyRiskRanking: Float!
	) {
		saveCalculatedNonLifeRatioStatement(
			year: $year
			companyType: $companyType
			companyId: $companyId
			data: $data
			requiredSolvency: $requiredSolvency
			companyRiskRanking: $companyRiskRanking
		)
	}
`;

const USER_INPUT_CODE = ['SRNL', 'CARNL'];

export default function EWSNonLifeSpreadSheetTable({ year = null, companyId = null, sfp = null, sploci = null, nonLifeDataExport = () => null }) {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const companyType = urlParams.get('companyType') || null;

	const [active, setActive] = useState(null);
	const [detail, setDetail] = useState(null);
	const { isOpen, onOpen, onClose } = useDisclosure();

	const [q1Cells, setQ1Cells] = useState([]);
	const [q2Cells, setQ2Cells] = useState([]);
	const [q3Cells, setQ3Cells] = useState([]);
	const [q4Cells, setQ4Cells] = useState([]);
	const [q5Cells, setQ5Cells] = useState([]);

	const [isEditing, setIsEditing] = useState(false);
	const [initialQ2Cells, setInitialQ2Cells] = useState([]);
	const [initialQ3Cells, setInitialQ3Cells] = useState([]);

	const ratioCells = [
		{ cells: q1Cells, setCells: setQ1Cells, index: 0 },
		{ cells: q2Cells, setCells: setQ2Cells, index: 1 },
		{ cells: q3Cells, setCells: setQ3Cells, index: 2 },
		{ cells: q4Cells, setCells: null, index: 3 },
		{ cells: q5Cells, setCells: null, index: 4 },
	];

	const [loadReport, { loading, error, data }] = useLazyQuery(Q_REPORT);
	const [saveNonLifeRatio, { loading: loadingSaveNonLifeRatio }] = useMutation(M_SAVE_NONLIFE_RATIO, {
		onCompleted: () => {
			toast({
				title: t('Ratio saved successfully'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
			});
		},
		onError: (err) => {
			const message = err?.graphQLErrors?.[0]?.message || err?.message || 'something went wrong';
			toast({
				title: t(message),
				status: 'error',
				isClosable: true,
				position: 'bottom-right',
			});
		},
	});

	function load() {
		const variables = {
			year: getYear(year),
			companyType: companyType,
			companyId,
		};
		loadReport({ variables });
	}

	useEffect(() => {
		load();
	}, [year, companyType, companyId]);

	useEffect(() => {
		if (data?.getEarlyWarningNonLifeRatioReport) {
			setDetail(data?.getEarlyWarningNonLifeRatioReport);
		}
	}, [data]);

	useEffect(() => {
		if (detail && sploci && sfp) {
			quarterDetail(detail);
		}
	}, [detail, sploci, sfp]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error.graphQLErrors.forEach(({ message }) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					})
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	const isQuarterObjectArray = (q) => Array.isArray(q) && q.length > 0 && !Array.isArray(q[0]) && q[0] && typeof q[0] === 'object' && 'code' in q[0];

	const isSpreadsheetCells = (q) =>
		Array.isArray(q) && q.length > 0 && Array.isArray(q[0]) && q[0].length > 0 && q[0][0] && Object.prototype.hasOwnProperty.call(q[0][0], 'value');

	async function calculateButton() {
		if (!detail) return;

		const variables = {
			year: getYear(year),
			companyType: companyType,
			companyId,
			data: {
				q2: q2Cells,
				q3: q3Cells,
				q4: q4Cells,
				q5: q5Cells,
			},
			requiredSolvency: 0,
			companyRiskRanking: 0,
		};

		await saveNonLifeRatio({ variables });

		nonLifeDataExport({
			labels: detail.labels,
			q1Cells,
			q2Cells,
			q3Cells,
			q4Cells,
			q5Cells,
		});
	}

	function emptyCells(labels = [], i, sploci = { cells: [] }, sfp = { cells: [] }) {
		const getCells = (labelCode) => {
			switch (labelCode) {
				case 'CIGPNL':
					return Array.isArray(sploci?.cells?.[2]) ? sploci.cells[2] : [];
				case 'CINPNL':
					return Array.isArray(sploci?.cells?.[8]) ? sploci.cells[8] : [];
				case 'CRNL':
					return [
						Array.isArray(sploci?.cells?.[10]) ? sploci.cells[10] : [],
						Array.isArray(sploci?.cells?.[18]) ? sploci.cells[18] : [],
						Array.isArray(sploci?.cells?.[25]) ? sploci.cells[25] : [],
					];
				case 'RRNL':
					return [Array.isArray(sploci?.cells?.[2]) ? sploci.cells[2] : [], Array.isArray(sploci?.cells?.[8]) ? sploci.cells[8] : []];
				case 'IYRNL':
					return [
						Array.isArray(sploci?.cells?.[32]) ? sploci.cells[32] : [],
						Array.isArray(sfp?.cells?.[2]) ? sfp.cells[2] : [],
						Array.isArray(sfp?.cells?.[3]) ? sfp.cells[3] : [],
						Array.isArray(sfp?.cells?.[8]) ? sfp.cells[8] : [],
						Array.isArray(sfp?.cells?.[9]) ? sfp.cells[9] : [],
						Array.isArray(sfp?.cells?.[10]) ? sfp.cells[10] : [],
						Array.isArray(sfp?.cells?.[11]) ? sfp.cells[11] : [],
						Array.isArray(sfp?.cells?.[12]) ? sfp.cells[12] : [],
						Array.isArray(sfp?.cells?.[13]) ? sfp.cells[13] : [],
					];
				case 'LRINL':
					return [
						Array.isArray(sfp?.cells?.[2]) ? sfp.cells[2] : [],
						Array.isArray(sfp?.cells?.[3]) ? sfp.cells[3] : [],
						Array.isArray(sfp?.cells?.[14]) ? sfp.cells[14] : [],
						Array.isArray(sfp?.cells?.[15]) ? sfp.cells[15] : [],
						Array.isArray(sfp?.cells?.[16]) ? sfp.cells[16] : [],
						Array.isArray(sfp?.cells?.[40]) ? sfp.cells[40] : [],
						Array.isArray(sfp?.cells?.[42]) ? sfp.cells[42] : [],
						Array.isArray(sfp?.cells?.[43]) ? sfp.cells[43] : [],
						Array.isArray(sfp?.cells?.[44]) ? sfp.cells[44] : [],
						Array.isArray(sfp?.cells?.[45]) ? sfp.cells[45] : [],
						Array.isArray(sfp?.cells?.[53]) ? sfp.cells[53] : [],
					];
				case 'PRRNL':
					return [
						Array.isArray(sfp?.cells?.[14]) ? sfp.cells[14] : [],
						Array.isArray(sfp?.cells?.[15]) ? sfp.cells[15] : [],
						Array.isArray(sfp?.cells?.[16]) ? sfp.cells[16] : [],
						Array.isArray(sfp?.cells?.[36]) ? sfp.cells[36] : [],
					];
				case 'GRRNL':
					return [Array.isArray(sploci?.cells?.[2]) ? sploci.cells[2] : [], Array.isArray(sfp?.cells?.[36]) ? sfp.cells[36] : []];
				case 'NRNL':
					return [Array.isArray(sploci?.cells?.[8]) ? sploci.cells[8] : [], Array.isArray(sfp?.cells?.[36]) ? sfp.cells[36] : []];
				case 'DRNL':
					return [Array.isArray(sfp?.cells?.[54]) ? sfp.cells[54] : [], Array.isArray(sfp?.cells?.[24]) ? sfp.cells[24] : []];
				case 'NOCTSENL':
					return [Array.isArray(sfp?.cells?.[42]) ? sfp.cells[42] : [], Array.isArray(sfp?.cells?.[36]) ? sfp.cells[36] : []];
				case 'NIRNL':
					return [Array.isArray(sploci?.cells?.[56]) ? sploci.cells[56] : [], Array.isArray(sploci?.cells?.[2]) ? sploci.cells[2] : []];
				case 'ROENL':
					return [Array.isArray(sploci?.cells?.[56]) ? sploci.cells[56] : [], Array.isArray(sfp?.cells?.[36]) ? sfp.cells[36] : []];
				case 'ROANL':
					return [Array.isArray(sploci?.cells?.[56]) ? sploci.cells[56] : [], Array.isArray(sfp?.cells?.[24]) ? sfp.cells[24] : []];
				default:
					return [];
			}
		};

		const calculateScore = (labelCode, cells, i) => {
			switch (labelCode) {
				case 'CIGPNL':
					const cigp1 = cells;
					const cigp1Cell = Number(cigp1[0]?.value || 0) / Number(cigp1[1]?.value || 0) - 1;
					const cigp2Cell = Number(cigp1[1]?.value || 0) / Number(cigp1[2]?.value || 0) - 1;
					const cigp1Pct = cigp1Cell * 100;
					const cigp2Pct = cigp2Cell * 100;
					const riskCigp1 =
						cigp1Pct <= -63
							? 4
							: cigp1Pct <= -53
							? 3
							: cigp1Pct <= -43
							? 2
							: cigp1Pct <= -33
							? 1
							: cigp1Pct <= 0
							? 0
							: cigp1Pct <= 33
							? 0
							: cigp1Pct <= 43
							? 1
							: cigp1Pct <= 53
							? 2
							: cigp1Pct <= 63
							? 3
							: cigp1Pct <= 73
							? 4
							: 0;
					const riskCigp2 =
						cigp2Pct <= -63
							? 4
							: cigp2Pct <= -53
							? 3
							: cigp2Pct <= -43
							? 2
							: cigp2Pct <= -33
							? 1
							: cigp2Pct <= 0
							? 0
							: cigp2Pct <= 33
							? 0
							: cigp2Pct <= 43
							? 1
							: cigp2Pct <= 53
							? 2
							: cigp2Pct <= 63
							? 3
							: cigp2Pct <= 73
							? 4
							: 0;
					switch (i) {
						case 0:
							return cigp1Cell;
						case 1:
							return cigp2Cell;
						case 3:
							return riskCigp1;
						case 4:
							return riskCigp2;
						default:
							return 0;
					}
				case 'CINPNL':
					const cinp1 = cells;
					const cinp1Cell = Number(cinp1[0]?.value || 0) / Number(cinp1[1]?.value || 0) - 1;
					const cinp2Cell = Number(cinp1[1]?.value || 0) / Number(cinp1[2]?.value || 0) - 1;
					const cinp1Pct = cinp1Cell * 100;
					const cinp2Pct = cinp2Cell * 100;
					const riskCinp1 =
						cinp1Pct <= -63
							? 4
							: cinp1Pct <= -53
							? 3
							: cinp1Pct <= -43
							? 2
							: cinp1Pct <= -33
							? 1
							: cinp1Pct <= 0
							? 0
							: cinp1Pct <= 33
							? 0
							: cinp1Pct <= 43
							? 1
							: cinp1Pct <= 53
							? 2
							: cinp1Pct <= 63
							? 3
							: cinp1Pct <= 73
							? 4
							: 0;
					const riskCinp2 =
						cinp2Pct <= -50
							? 4
							: cinp2Pct <= -45
							? 3
							: cinp2Pct <= -40
							? 2
							: cinp2Pct <= -35
							? 1
							: cinp2Pct <= 30
							? 0
							: cinp2Pct <= 30
							? 0
							: cinp2Pct <= 35
							? 1
							: cinp2Pct <= 40
							? 2
							: cinp2Pct <= 45
							? 3
							: cinp2Pct <= 50
							? 4
							: 0;
					switch (i) {
						case 0:
							return cinp1Cell;
						case 1:
							return cinp2Cell;
						case 2:
							return riskCinp1;
						case 3:
							return riskCinp2;
						default:
							return 0;
					}
				case 'CRNL': {
					const [TOI, NCBE, TOE] = cells;
					const cr1Cell = -((Number(TOE[0]?.value || 0) + Number(NCBE[0]?.value || 0)) / Number(TOI[0]?.value || 0));
					const cr2Cell = -((Number(TOE[1]?.value || 0) + Number(NCBE[1]?.value || 0)) / Number(TOI[1]?.value || 0));
					const cr1Pct = cr1Cell * 100;
					const cr2Pct = cr2Cell * 100;
					const riskCr1 = cr1Pct <= 90 ? 0 : cr1Pct <= 100 ? 1 : cr1Pct <= 110 ? 2 : cr1Pct <= 120 ? 3 : 4;
					const riskCr2 = cr2Pct <= 90 ? 0 : cr2Pct <= 100 ? 1 : cr2Pct <= 110 ? 2 : cr2Pct <= 120 ? 3 : 4;
					switch (i) {
						case 0:
							return cr1Cell;
						case 1:
							return cr2Cell;
						case 2:
							return riskCr1;
						case 3:
							return riskCr2;
						default:
							return 0;
					}
				}
				case 'RRNL': {
					const [GPI, NPE] = cells;
					const rr1Cell = Number(NPE[0]?.value || 0) / Number(GPI[0]?.value || 0);
					const rr2Cell = Number(NPE[1]?.value || 0) / Number(GPI[1]?.value || 0);
					const rr1Pct = rr1Cell * 100;
					const rr2Pct = rr2Cell * 100;
					const riskRr1 = rr1Pct >= 50 ? 0 : rr1Pct >= 40 ? 1 : rr1Pct >= 30 ? 2 : rr1Pct >= 20 ? 3 : 4;
					const riskRr2 = rr2Pct >= 50 ? 0 : rr2Pct >= 40 ? 1 : rr2Pct >= 30 ? 2 : rr2Pct >= 20 ? 3 : 4;
					switch (i) {
						case 0:
							return rr1Cell;
						case 1:
							return rr2Cell;
						case 2:
							return riskRr1;
						case 3:
							return riskRr2;
						default:
							return 0;
					}
				}
				case 'IYRNL': {
					const [iyr1, iyr2, iyr3, iyr4, iyr5, iyr6, iyr7, iyr8, iyr9] = cells;
					const iyr1Cell =
						Number(iyr1[0]?.value || 0) /
						(Number(iyr2[0]?.value || 0) +
							(Number(iyr3[0]?.value || 0) +
								Number(iyr4[0]?.value || 0) +
								Number(iyr5[0]?.value || 0) +
								Number(iyr6[0]?.value || 0) +
								Number(iyr7[0]?.value || 0) +
								Number(iyr8[0]?.value || 0) +
								Number(iyr9[0]?.value || 0) +
								(Number(iyr2[1]?.value || 0) +
									Number(iyr3[1]?.value || 0) +
									Number(iyr4[1]?.value || 0) +
									Number(iyr5[1]?.value || 0) +
									Number(iyr6[1]?.value || 0) +
									Number(iyr7[1]?.value || 0) +
									Number(iyr8[1]?.value || 0) +
									Number(iyr9[1]?.value || 0)) /
									2));
					const iyr2Cell =
						Number(iyr1[1]?.value || 0) /
						(Number(iyr2[1]?.value || 0) +
							(Number(iyr3[1]?.value || 0) +
								Number(iyr4[1]?.value || 0) +
								Number(iyr5[1]?.value || 0) +
								Number(iyr6[1]?.value || 0) +
								Number(iyr7[1]?.value || 0) +
								Number(iyr8[1]?.value || 0) +
								Number(iyr9[1]?.value || 0) +
								(Number(iyr2[2]?.value || 0) +
									Number(iyr3[2]?.value || 0) +
									Number(iyr4[2]?.value || 0) +
									Number(iyr5[2]?.value || 0) +
									Number(iyr6[2]?.value || 0) +
									Number(iyr7[2]?.value || 0) +
									Number(iyr8[2]?.value || 0) +
									Number(iyr9[2]?.value || 0)) /
									2));
					const iyr1Pct = iyr1Cell * 100;
					const iyr2Pct = iyr2Cell * 100;
					const riskIyr1 =
						iyr1Pct <= -27
							? 4
							: iyr1Pct <= -17
							? 3
							: iyr1Pct <= -7
							? 2
							: iyr1Pct <= 3
							? 1
							: iyr1Pct === 3
							? 0
							: iyr1Pct <= 10
							? 0
							: iyr1Pct <= 20
							? 1
							: iyr1Pct <= 30
							? 2
							: iyr1Pct <= 40
							? 3
							: iyr1Pct <= 50
							? 4
							: 0;
					const riskIyr2 =
						iyr2Pct <= -27
							? 4
							: iyr2Pct <= -17
							? 3
							: iyr2Pct <= -7
							? 2
							: iyr2Pct <= 3
							? 1
							: iyr2Pct === 3
							? 0
							: iyr2Pct <= 10
							? 0
							: iyr2Pct <= 20
							? 1
							: iyr2Pct <= 30
							? 2
							: iyr2Pct <= 40
							? 3
							: iyr2Pct <= 50
							? 4
							: 0;
					switch (i) {
						case 0:
							return iyr1Cell;
						case 1:
							return iyr2Cell;
						case 2:
							return riskIyr1;
						case 3:
							return riskIyr2;
						default:
							return 0;
					}
				}
				case 'LRINL': {
					const [li1, li2, li3, li4, li5, li6, li7, li8, li9, li10, li11] = cells;
					const lr1Cell =
						(Number(li1[0]?.value || 0) +
							Number(li2[0]?.value || 0) +
							Number(li3[0]?.value || 0) +
							Number(li4[0]?.value || 0) +
							Number(li5[0]?.value || 0)) /
						(Number(li6[0]?.value || 0) +
							Number(li7[0]?.value || 0) +
							Number(li8[0]?.value || 0) +
							Number(li9[0]?.value || 0) +
							Number(li10[0]?.value || 0) +
							Number(li11[0]?.value || 0));
					const lr2Cell =
						(Number(li1[1]?.value || 0) +
							Number(li2[1]?.value || 0) +
							Number(li3[1]?.value || 0) +
							Number(li4[1]?.value || 0) +
							Number(li5[1]?.value || 0)) /
						(Number(li6[1]?.value || 0) +
							Number(li7[1]?.value || 0) +
							Number(li8[1]?.value || 0) +
							Number(li9[1]?.value || 0) +
							Number(li10[1]?.value || 0) +
							Number(li11[1]?.value || 0));
					const lr1Pct = lr1Cell * 100;
					const lr2Pct = lr2Cell * 100;
					const riskLr1 = lr1Pct >= 110 ? 0 : lr1Pct >= 100 ? 1 : lr1Pct >= 90 ? 2 : lr1Pct >= 80 ? 3 : 4;
					const riskLr2 = lr2Pct >= 110 ? 0 : lr2Pct >= 100 ? 1 : lr2Pct >= 90 ? 2 : lr2Pct >= 80 ? 3 : 4;
					switch (i) {
						case 0:
							return lr1Cell;
						case 1:
							return lr2Cell;
						case 2:
							return riskLr1;
						case 3:
							return riskLr2;
						default:
							return 0;
					}
				}
				case 'PRRNL': {
					const [pr1, pr2, pr3, pr4] = cells;
					const pr1Cell = (Number(pr1[0]?.value || 0) + Number(pr2[0]?.value || 0) + Number(pr3[0]?.value || 0)) / Number(pr4[0]?.value || 0);
					const pr2Cell = (Number(pr1[1]?.value || 0) + Number(pr2[1]?.value || 0) + Number(pr3[1]?.value || 0)) / Number(pr4[1]?.value || 0);
					const pr1Pct = pr1Cell * 100;
					const pr2Pct = pr2Cell * 100;
					const riskPr1 = pr1Pct <= 50 ? 0 : pr1Pct <= 60 ? 1 : pr1Pct <= 70 ? 2 : pr1Pct <= 80 ? 3 : 4;
					const riskPr2 = pr2Pct <= 50 ? 0 : pr2Pct <= 60 ? 1 : pr2Pct <= 70 ? 2 : pr2Pct <= 80 ? 3 : 4;
					switch (i) {
						case 0:
							return pr1Cell;
						case 1:
							return pr2Cell;
						case 2:
							return riskPr1;
						case 3:
							return riskPr2;
						default:
							return 0;
					}
				}
				case 'GRRNL': {
					const [GPI, TE] = cells;
					const gr1Cell = Number(GPI[0]?.value || 0) / Number(TE[0]?.value || 0);
					const gr2Cell = Number(GPI[1]?.value || 0) / Number(TE[1]?.value || 0);
					const gr1Pct = gr1Cell * 100;
					const gr2Pct = gr2Cell * 100;
					const riskGr1 = gr1Pct <= 500 ? 0 : gr1Pct <= 600 ? 1 : gr1Pct <= 700 ? 2 : gr1Pct <= 800 ? 3 : 4;
					const riskGr2 = gr2Pct <= 500 ? 0 : gr2Pct <= 600 ? 1 : gr2Pct <= 700 ? 2 : gr2Pct <= 800 ? 3 : 4;
					switch (i) {
						case 0:
							return gr1Cell;
						case 1:
							return gr2Cell;
						case 2:
							return riskGr1;
						case 3:
							return riskGr2;
						default:
							return 0;
					}
				}
				case 'NRNL': {
					const [NPE, TE] = cells;
					const nr1Cell = Number(NPE[0]?.value || 0) / Number(TE[0]?.value || 0);
					const nr2Cell = Number(NPE[1]?.value || 0) / Number(TE[1]?.value || 0);
					const nr1Pct = nr1Cell * 100;
					const nr2Pct = nr2Cell * 100;
					const riskNr1 = nr1Pct <= 300 ? 0 : nr1Pct <= 400 ? 1 : nr1Pct <= 500 ? 2 : nr1Pct <= 600 ? 3 : 4;
					const riskNr2 = nr2Pct <= 300 ? 0 : nr2Pct <= 400 ? 1 : nr2Pct <= 500 ? 2 : nr2Pct <= 600 ? 3 : 4;
					switch (i) {
						case 0:
							return nr1Cell;
						case 1:
							return nr2Cell;
						case 2:
							return riskNr1;
						case 3:
							return riskNr2;
						default:
							return 0;
					}
				}
				case 'DRNL': {
					const [dr1, dr2] = cells;
					const dr1Cell = Number(dr1[0]?.value || 0) / Number(dr2[0]?.value || 0);
					const dr2Cell = Number(dr1[1]?.value || 0) / Number(dr2[1]?.value || 0);
					const dr1Pct = dr1Cell * 100;
					const dr2Pct = dr2Cell * 100;
					const riskDr1 =
						dr1Pct <= 0
							? 4
							: dr1Pct <= 10
							? 3
							: dr1Pct <= 20
							? 2
							: dr1Pct < 30
							? 1
							: dr1Pct === 40
							? 0
							: dr1Pct <= 60
							? 0
							: dr1Pct <= 70
							? 1
							: dr1Pct <= 80
							? 2
							: dr1Pct <= 90
							? 3
							: dr1Pct <= 100 || dr1Pct > 100
							? 4
							: 0;
					const riskDr2 =
						dr2Pct <= 0
							? 4
							: dr2Pct <= 10
							? 3
							: dr2Pct <= 20
							? 2
							: dr2Pct < 30
							? 1
							: dr2Pct === 40
							? 0
							: dr2Pct <= 60
							? 0
							: dr2Pct <= 70
							? 1
							: dr2Pct <= 80
							? 2
							: dr2Pct <= 90
							? 3
							: dr2Pct <= 100 || dr2Pct > 100
							? 4
							: 0;

					switch (i) {
						case 0:
							return dr1Cell;
						case 1:
							return dr2Cell;
						case 2:
							return riskDr1;
						case 3:
							return riskDr2;
						default:
							return 0;
					}
				}
				case 'NOCTSENL': {
					const [noct1, noct2] = cells;
					const noct1Cell = Number(noct1[0]?.value || 0) / Number(noct2[0]?.value || 0);
					const noct2Cell = Number(noct1[1]?.value || 0) / Number(noct2[1]?.value || 0);
					const noct1Pct = noct1Cell * 100;
					const noct2Pct = noct2Cell * 100;
					const riskNoct1 = noct1Pct <= 250 ? 0 : noct1Pct <= 350 ? 1 : noct1Pct <= 450 ? 2 : noct1Pct <= 550 ? 3 : 4;
					const riskNoct2 = noct2Pct <= 250 ? 0 : noct2Pct <= 350 ? 1 : noct2Pct <= 450 ? 2 : noct2Pct <= 550 ? 3 : 4;
					switch (i) {
						case 0:
							return noct1Cell;
						case 1:
							return noct2Cell;
						case 2:
							return riskNoct1;
						case 3:
							return riskNoct2;
						default:
							return 0;
					}
				}
				case 'NIRNL': {
					const [nr1, nr2] = cells;
					const nr1Cell = Number(nr1[0]?.value || 0) / Number(nr2[0]?.value || 0);
					const nr2Cell = Number(nr1[1]?.value || 0) / Number(nr2[1]?.value || 0);
					const nr1Pct = nr1Cell * 100;
					const nr2Pct = nr2Cell * 100;
					const riskNr1 =
						nr1Pct <= -30
							? 4
							: nr1Pct <= -20
							? 3
							: nr1Pct <= -10
							? 2
							: nr1Pct < 0
							? 1
							: nr1Pct === 0
							? 0
							: nr1Pct <= 10
							? 0
							: nr1Pct <= 20
							? 1
							: nr1Pct <= 30
							? 2
							: nr1Pct <= 40
							? 3
							: nr1Pct <= 50
							? 4
							: 0;
					const riskNr2 =
						nr2Pct <= -30
							? 4
							: nr2Pct <= -20
							? 3
							: nr2Pct <= -10
							? 2
							: nr2Pct < 0
							? 1
							: nr2Pct === 0
							? 0
							: nr2Pct <= 10
							? 0
							: nr2Pct <= 20
							? 1
							: nr2Pct <= 30
							? 2
							: nr2Pct <= 40
							? 3
							: nr2Pct <= 50
							? 4
							: 0;
					switch (i) {
						case 0:
							return nr1Cell;
						case 1:
							return nr2Cell;
						case 2:
							return riskNr1;
						case 3:
							return riskNr2;
						default:
							return 0;
					}
				}
				case 'ROENL': {
					const [roe1, roe2] = cells;
					const roe1Cell = Number(roe1[0]?.value || 0) / Number(roe2[0]?.value || 0);
					const roe2Cell = Number(roe1[1]?.value || 0) / Number(roe2[1]?.value || 0);
					const roe1Pct = roe1Cell * 100;
					const roe2Pct = roe2Cell * 100;
					const riskRoe1 =
						roe1Pct <= -20
							? 4
							: roe1Pct <= -10
							? 3
							: roe1Pct <= 0
							? 2
							: roe1Pct < 10
							? 1
							: roe1Pct === 10
							? 0
							: roe1Pct <= 15
							? 0
							: roe1Pct <= 25
							? 1
							: roe1Pct <= 35
							? 2
							: roe1Pct <= 45
							? 3
							: roe1Pct <= 55 || roe1Pct > 55
							? 4
							: 0;
					const riskRoe2 =
						roe2Pct <= -20
							? 4
							: roe2Pct <= -10
							? 3
							: roe2Pct <= 0
							? 2
							: roe2Pct < 10
							? 1
							: roe2Pct === 10
							? 0
							: roe2Pct <= 15
							? 0
							: roe2Pct <= 25
							? 1
							: roe2Pct <= 35
							? 2
							: roe2Pct <= 45
							? 3
							: roe2Pct <= 55 || roe2Pct > 55
							? 4
							: 0;
					switch (i) {
						case 0:
							return roe1Cell;
						case 1:
							return roe2Cell;
						case 2:
							return riskRoe1;
						case 3:
							return riskRoe2;
						default:
							return 0;
					}
				}
				case 'ROANL': {
					const [roa1, roa2] = cells;
					const roa1Cell = Number(roa1[0]?.value || 0) / Number(roa2[0]?.value || 0);
					const roa2Cell = Number(roa1[1]?.value || 0) / Number(roa2[1]?.value || 0);
					const roa1Pct = roa1Cell * 100;
					const roa2Pct = roa2Cell * 100;
					const riskRoa1 =
						roa1Pct <= -25
							? 4
							: roa1Pct <= -15
							? 3
							: roa1Pct <= -5
							? 2
							: roa1Pct < 5
							? 1
							: roa1Pct === 5
							? 0
							: roa1Pct <= 20
							? 0
							: roa1Pct <= 30
							? 1
							: roa1Pct <= 40
							? 2
							: roa1Pct <= 50
							? 3
							: roa1Pct <= 60
							? 4
							: 0;
					const riskRoa2 =
						roa2Pct <= -25
							? 4
							: roa2Pct <= -15
							? 3
							: roa2Pct <= -5
							? 2
							: roa2Pct < 5
							? 1
							: roa2Pct === 5
							? 0
							: roa2Pct <= 20
							? 0
							: roa2Pct <= 30
							? 1
							: roa2Pct <= 40
							? 2
							: roa2Pct <= 50
							? 3
							: roa2Pct <= 60
							? 4
							: 0;
					switch (i) {
						case 0:
							return roa1Cell;
						case 1:
							return roa2Cell;
						case 2:
							return riskRoa1;
						case 3:
							return riskRoa2;
						default:
							return 0;
					}
				}
				default:
					return 0;
			}
		};

		return labels?.map((label) => {
			if (label?.readOnly) return [{ value: '', readOnly: true }];

			const cells = getCells(label?.code);
			const score = calculateScore(label?.code, cells, i);

			if (i === 0 || i === 1) {
				const percent = Number.isFinite(score) ? (score * 100).toFixed(2) + '%' : '';

				return [
					{
						value: percent,
						readOnly: true,
						style: {
							textAlign: 'center',
							fontWeight: 'bold',
						},
					},
				];
			}

			const riskValue = score.toFixed(2);

			return [
				{
					value: riskValue,
					readOnly: true,
					style: {
						textAlign: 'center',
						fontWeight: 'bold',
					},
				},
			];
		});
	}

	function quarterCells({ labels = [], quarter = [] }) {
		const localCells = labels?.map((label) => {
			const rowForLabel = quarter?.find((q) => q?.code === label?.code);
			const fallbackValues = [{ value: '', readOnly: true }];
			return rowForLabel?.values || fallbackValues;
		});

		return localCells;
	}

	function normCells({ labels = [] }) {
		return labels?.map((label) => {
			const normValue = label?.normsValue || '';
			return [{ value: normValue, readOnly: true, style: { textAlign: 'center' } }];
		});
	}

	const formatUserInputCellsAsPercent = (labels, cells) => {
		if (!Array.isArray(cells)) return cells;

		return cells?.map((row, rowIndex) => {
			const code = labels?.[rowIndex]?.code;
			if (!USER_INPUT_CODE?.includes(code)) {
				return row;
			}

			return row?.map((cell) => {
				if (!cell) return cell;
				const raw = cell?.value;

				if (raw === '' || raw == null) return cell;

				const str = String(raw).trim();

				if (str.endsWith('%')) return cell;

				const num = parseFloat(str);
				if (!Number.isFinite(num)) return cell;

				return {
					...cell,
					value: `${num.toFixed(2)}%`,
				};
			});
		});
	};

	function quarterDetail(detailData) {
		if (!detailData || !detailData?.labels) return;

		const labels = detailData?.labels;
		const normsCell = normCells({ labels });

		// q2: Current Ratio
		let currentRatioCellsRaw;
		if (isSpreadsheetCells(detailData?.q2)) {
			currentRatioCellsRaw = detailData?.q2;
		} else if (isQuarterObjectArray(detailData?.q2)) {
			currentRatioCellsRaw = quarterCells({ labels, quarter: detailData?.q2 });
		} else {
			currentRatioCellsRaw = emptyCells(labels, 0, sploci, sfp);
		}

		// q3: Previous Ratio
		let previousRatioCellsRaw;
		if (isSpreadsheetCells(detailData?.q3)) {
			previousRatioCellsRaw = detailData?.q3;
		} else if (isQuarterObjectArray(detailData?.q3)) {
			previousRatioCellsRaw = quarterCells({ labels, quarter: detailData?.q3 });
		} else {
			previousRatioCellsRaw = emptyCells(labels, 1, sploci, sfp);
		}

		// q4: Current Risk
		let currentRiskFlagCellsRaw;
		if (isSpreadsheetCells(detailData?.q4)) {
			currentRiskFlagCellsRaw = detailData?.q4;
		} else {
			currentRiskFlagCellsRaw = emptyCells(labels, 2, sploci, sfp);
		}

		// q5: Previous Risk
		let previousRiskFlagCellsRaw;
		if (isSpreadsheetCells(detailData?.q5)) {
			previousRiskFlagCellsRaw = detailData?.q5;
		} else {
			previousRiskFlagCellsRaw = emptyCells(labels, 3, sploci, sfp);
		}

		const numRows = detailData?.labels?.length;
		const normalize = (cells) => (cells?.length === numRows ? cells : [...cells, ...Array(numRows - cells?.length).fill([{ value: '', readOnly: true }])]);

		const makeManualEditable = (cells) =>
			cells?.map((row, rowIndex) => {
				const code = labels?.[rowIndex]?.code;
				if (USER_INPUT_CODE?.includes(code)) {
					return row?.map((cell) => ({
						...cell,
						readOnly: false,
					}));
				}
				return row;
			});

		const normsCellNorm = normalize(normsCell);
		const currentRatioFormatted = formatUserInputCellsAsPercent(labels, currentRatioCellsRaw);
		const previousRatioFormatted = formatUserInputCellsAsPercent(labels, previousRatioCellsRaw);

		const currentRatioCells = normalize(makeManualEditable(currentRatioFormatted));
		const previousRatioCells = normalize(makeManualEditable(previousRatioFormatted));
		const currentRiskFlagCells = normalize(currentRiskFlagCellsRaw);
		const previousRiskFlagCells = normalize(previousRiskFlagCellsRaw);

		setQ1Cells(normsCellNorm);
		setQ2Cells(currentRatioCells);
		setQ3Cells(previousRatioCells);
		setQ4Cells(currentRiskFlagCells);
		setQ5Cells(previousRiskFlagCells);

		setInitialQ2Cells(currentRatioCells);
		setInitialQ3Cells(previousRatioCells);
		setIsEditing(false);

		nonLifeDataExport({
			labels,
			q1Cells: normsCell,
			q2Cells: currentRatioCells,
			q3Cells: previousRatioCells,
			q4Cells: currentRiskFlagCells,
			q5Cells: previousRiskFlagCells,
		});
	}

	const parseRatioValue = (cellValue) => {
		if (cellValue == null) return 0;
		if (typeof cellValue === 'number') return cellValue;

		const cleaned = String(cellValue).replace('%', '').trim();
		const num = parseFloat(cleaned);
		return Number.isFinite(num) ? num : 0;
	};

	const getRiskLevelForUserInput = (code, ratio) => {
		switch (code) {
			case 'SRNL':
				if (ratio >= 120) return 0;
				if (ratio >= 110) return 1;
				if (ratio >= 110) return 2;
				if (ratio >= 90) return 3;
				return 4;

			case 'CARNL':
				if (ratio >= 100) return 0;
				if (ratio >= 90) return 1;
				if (ratio >= 80) return 2;
				if (ratio >= 70) return 3;
				return 4;

			default:
				return 0;
		}
	};

	// Current Risk Flag (q4)
	useEffect(() => {
		if (!detail?.labels || !q2Cells?.length) return;

		const base = q4Cells?.length
			? q4Cells
			: detail.labels.map(() => [
					{
						value: '',
						readOnly: true,
						style: { textAlign: 'center', fontWeight: 'bold' },
					},
			  ]);

		const updated = detail?.labels?.map((label, rowIndex) => {
			const baseCell = base[rowIndex]?.[0] || {
				value: '',
				readOnly: true,
				style: { textAlign: 'center', fontWeight: 'bold' },
			};

			if (!USER_INPUT_CODE?.includes(label?.code)) {
				return [baseCell];
			}

			const ratioCell = q2Cells?.[rowIndex]?.[0] || { value: '' };
			const ratioNumber = parseRatioValue(ratioCell?.value);
			const riskLevel = getRiskLevelForUserInput(label?.code, ratioNumber);

			return [
				{
					...baseCell,
					value: riskLevel,
					readOnly: true,
				},
			];
		});

		setQ4Cells(updated);
	}, [q2Cells, detail]);

	// Previous Risk Flag (q5)
	useEffect(() => {
		if (!detail?.labels || !q3Cells?.length) return;

		const base = q5Cells?.length
			? q5Cells
			: detail.labels.map(() => [
					{
						value: '',
						readOnly: true,
						style: { textAlign: 'center', fontWeight: 'bold' },
					},
			  ]);

		const updated = detail?.labels?.map((label, rowIndex) => {
			const baseCell = base[rowIndex]?.[0] || {
				value: '',
				readOnly: true,
				style: { textAlign: 'center', fontWeight: 'bold' },
			};

			if (!USER_INPUT_CODE.includes(label?.code)) {
				return [baseCell];
			}

			const ratioCell = q3Cells?.[rowIndex]?.[0] || { value: '' };
			const ratioNumber = parseRatioValue(ratioCell.value);
			const riskLevel = getRiskLevelForUserInput(label.code, ratioNumber);

			return [
				{
					...baseCell,
					value: riskLevel,
					readOnly: true,
				},
			];
		});

		setQ5Cells(updated);
	}, [q3Cells, detail]);

	function handleCellsChange(index, newData) {
		switch (index) {
			case 0:
				setQ1Cells(newData);
				break;
			case 1:
				setQ2Cells(newData);
				break;
			case 2:
				setQ3Cells(newData);
				break;
			case 3:
				setQ4Cells(newData);
				break;
			case 4:
				setQ5Cells(newData);
				break;
			default:
				break;
		}
	}

	const isDifferent = (a = [], b = []) => {
		if (!a || !b) return true;
		if (a?.length !== b?.length) return true;
		return a?.some((row, rIndex) => row?.some((cell, cIndex) => cell?.value !== b[rIndex]?.[cIndex]?.value));
	};

	return (
		<Box>
			{loading && <Loading />}

			{!loading && !detail && (
				<Text color="gray.400" textAlign="center" mt={4}>
					{t('No Data')}
				</Text>
			)}

			{!loading && detail && (
				<Box className="ews-container">
					<Flex mb={2} justifyContent="flex-end" alignItems="center">
						{isEditing && (
							<>
								<Button
									variant="solid"
									size="md"
									h="40px"
									w="128px"
									mb={4}
									bg="green"
									_hover={{ bg: 'darkgreen' }}
									color="white"
									rightIcon={<FontAwesomeIcon icon={faCalculator} style={{ fontSize: 16 }} />}
									onClick={onOpen}
									isLoading={loadingSaveNonLifeRatio}
									spinnerPlacement="start"
								>
									{t('Re-Calculate')}
								</Button>
								<Button
									variant="solid"
									size="md"
									h="40px"
									w="120px"
									ml={2}
									mb={4}
									bg="red"
									_hover={{ bg: 'darkred' }}
									color="white"
									rightIcon={<FontAwesomeIcon icon={faTimes} style={{ fontSize: 16 }} />}
									onClick={() => {
										setQ2Cells(initialQ2Cells);
										setQ3Cells(initialQ3Cells);
										setIsEditing(false);
									}}
									isLoading={loadingSaveNonLifeRatio}
									spinnerPlacement="start"
								>
									{t('Cancel')}
								</Button>
							</>
						)}
					</Flex>
					<div className="ews-1-row-head-ratio">{t('Ratio Name')}</div>

					<Flex>
						{q1Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											{t('Norms')}
										</td>
									</tr>
								</thead>
							</table>
						)}

						{q2Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											{t('Current Ratio')}
										</td>
									</tr>
								</thead>
							</table>
						)}

						{q3Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											{t('Previous Ratio')}
										</td>
									</tr>
								</thead>
							</table>
						)}

						{q4Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											{t('Current Risk Flag')}
										</td>
									</tr>
								</thead>
							</table>
						)}

						{q5Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											{t('Previous Risk Flag')}
										</td>
									</tr>
								</thead>
							</table>
						)}
					</Flex>

					<div>
						<EWSLabels type="EWS" labels={detail?.labels} active={active} />
						<Flex>
							{ratioCells?.map(({ cells, setCells, index }) =>
								cells?.length > 0 ? (
									<Spreadsheet
										key={index}
										data={cells}
										onChange={(newData) => {
											if (setCells) {
												handleCellsChange(index, newData);
											}

											if (index === 1) {
												const changed = isDifferent(newData, initialQ2Cells) || isDifferent(newData, initialQ3Cells);
												setIsEditing(changed);
											} else if (index === 2) {
												const changed = isDifferent(newData, initialQ3Cells) || isDifferent(newData, initialQ2Cells);
												setIsEditing(changed);
											}
										}}
										onActivate={(e) => setActive({ ...e, column: e?.column + 0, t: 'EWS' })}
										hideRowIndicators
										hideColumnIndicators
									/>
								) : null
							)}
						</Flex>
					</div>
				</Box>
			)}
			<AlertDialog isOpen={isOpen} leastDestructiveRef={undefined} onClose={onClose}>
				<AlertDialogOverlay />
				<AlertDialogContent>
					<AlertDialogHeader fontSize="lg" fontWeight="bold">
						Re-Calculate
					</AlertDialogHeader>

					<AlertDialogBody>
						<Text fontSize="16px">Are you sure you want to re-calculate the ratios?</Text>
					</AlertDialogBody>

					<AlertDialogFooter>
						<Button onClick={onClose}>Cancel</Button>
						<Button
							colorScheme="green"
							onClick={() => {
								calculateButton();
								onClose();
								window.location.reload();
							}}
							ml={3}
						>
							{t('Confirm')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</Box>
	);
}
